import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Card, CardContent, Typography, Button, TextField, 
  FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Chip, Grid, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import apiClient from '@/api/client';
import dayjs from 'dayjs';
import MemberConflictResolutionDialog from './MemberConflictResolutionDialog';

const statusOptions = ['pending', 'approved', 'rejected', 'merged'];
const statusColors = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  merged: 'default',
};

const MemberConflicts = () => {
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });
  const [filters, setFilters] = useState({ status: 'pending', search: '', date_from: '', date_to: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedConflict, setSelectedConflict] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchConflicts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        ...filters,
      };
      Object.keys(params).forEach(key => !params[key] && delete params[key]);
      const res = await apiClient.get('/admin/conflicts', { params });
      setConflicts(res.data.data.data);
      setTotalCount(res.data.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filters]);

  useEffect(() => {
    fetchConflicts();
  }, [fetchConflicts]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const clearFilters = () => {
    setFilters({ status: 'pending', search: '', date_from: '', date_to: '' });
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const openResolutionDialog = (conflict) => {
    setSelectedConflict(conflict);
    setDialogOpen(true);
  };

  const handleDialogClose = (refetch = false) => {
    setDialogOpen(false);
    setSelectedConflict(null);
    if (refetch) fetchConflicts();
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'incoming_name',
      headerName: 'Incoming Name',
      width: 180,
      renderCell: (params) => {
        if (!params || !params.row) return '—';
        const row = params.row;
        return `${row.incoming_first_name || ''} ${row.incoming_last_name || ''}`.trim() || '—';
      },
    },
    {
      field: 'incoming_email',
      headerName: 'Email',
      width: 200,
      renderCell: (params) => params?.row?.incoming_email || '—',
    },
    {
      field: 'incoming_mobile',
      headerName: 'Mobile',
      width: 130,
      renderCell: (params) => params?.row?.incoming_mobile || '—',
    },
    {
      field: 'matched_member',
      headerName: 'Matched Member',
      width: 200,
      renderCell: (params) => {
        if (!params || !params.row) return '—';
        const row = params.row;
        if (row.matched_member) {
          return `${row.matched_member.first_name} ${row.matched_member.last_name} (${row.matched_member.member_no})`;
        }
        if (row.matched_member_id_2) {
          return 'Cross‑member conflict';
        }
        return '—';
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 180,
      renderCell: (params) => {
        if (!params || !params.value) return '—';
        return dayjs(params.value).format('YYYY-MM-DD HH:mm');
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        if (!params || !params.value) return <Chip label="—" size="small" />;
        return (
          <Chip 
            label={params.value} 
            color={statusColors[params.value] || 'default'} 
            size="small" 
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        return (
          <Button 
            variant="outlined" 
            size="small" 
            onClick={() => openResolutionDialog(params.row)}
            disabled={params.row.status !== 'pending'}
          >
            Resolve
          </Button>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h5">Member Conflicts ({totalCount})</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={() => setShowFilters(!showFilters)} 
                startIcon={<FilterListIcon />}
              >
                Filters
              </Button>
              <Button variant="outlined" onClick={fetchConflicts} startIcon={<RefreshIcon />} disabled={loading}>
                Refresh
              </Button>
            </Box>
          </Box>
          {showFilters && (
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    {statusOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Search"
                  size="small"
                  fullWidth
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Name, email, mobile..."
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Date From"
                  type="date"
                  size="small"
                  fullWidth
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Date To"
                  type="date"
                  size="small"
                  fullWidth
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="text" onClick={clearFilters} startIcon={<ClearIcon />} size="small">
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: "text.secondary" }}>Pending Conflicts</Typography>
          <hr />
          <Box sx={{ height: 'calc(100vh - 350px)', width: '100%', mt: 2 }}>
            <DataGrid
              rows={conflicts}
              columns={columns}
              loading={loading}
              paginationMode="server"
              rowCount={totalCount}
              page={paginationModel.page}
              pageSize={paginationModel.pageSize}
              onPageChange={(newPage) => setPaginationModel(prev => ({ ...prev, page: newPage }))}
              onPageSizeChange={(newSize) => setPaginationModel({ page: 0, pageSize: newSize })}
              rowsPerPageOptions={[15, 30, 50]}
              disableRowSelectionOnClick
              getRowId={(row) => row.id}
              sx={{
                '& .MuiDataGrid-cell': { py: 0.5, fontSize: '0.875rem' },
                '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f5f5f5', fontWeight: 600 },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <MemberConflictResolutionDialog
        open={dialogOpen}
        conflict={selectedConflict}
        onClose={handleDialogClose}
      />
    </Box>
  );
};

export default MemberConflicts;